import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Briefcase, MapPin, CheckCircle, Loader2, Users, Heart, Globe } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const openings = [
  { title: "Medical Coordinator", type: "Full-time", location: "Noida, India", desc: "Manage international patient cases, coordinate with hospitals, and ensure seamless patient journeys." },
  { title: "Patient Relations Executive", type: "Full-time", location: "Noida / Remote", desc: "First point of contact for international patients. Fluency in English and Arabic preferred." },
  { title: "Digital Marketing Specialist", type: "Full-time", location: "Noida, India", desc: "Drive organic and paid growth for medical tourism services across global markets." },
  { title: "Hospital Partnership Manager", type: "Full-time", location: "Mumbai / Delhi", desc: "Build and maintain relationships with partner hospitals and specialists." },
  { title: "Wellness Program Coordinator", type: "Full-time", location: "Wada, Maharashtra", desc: "Coordinate wellness and Ayurvedic retreat programs at Govardhan Eco-village." },
  { title: "Country Representative - Africa", type: "Remote", location: "Nigeria / Kenya / Ghana", desc: "Represent Paxgrow in African markets, build patient referral networks." },
];

export default function CareersPage() {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [form, setForm] = useState({ name:"", email:"", phone:"", position:"", experience:"", message:"" });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/careers`, form);
      setSuccess(true);
      setForm({ name:"", email:"", phone:"", position:"", experience:"", message:"" });
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  return (
    <div data-testid="careers-page">
      <section className="bg-gradient-to-b from-orange-50 to-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Badge className="bg-orange-100 text-orange-700 border-orange-200 mb-4 font-body text-xs uppercase tracking-wider">Join Our Mission</Badge>
          <h1 className="font-heading text-4xl sm:text-5xl font-semibold text-slate-900 mb-4" data-testid="careers-title">Careers at Paxgrow</h1>
          <p className="text-base md:text-lg text-slate-500 max-w-2xl mx-auto">Help us make world-class healthcare accessible to global citizens. Join a team driven by compassion and excellence.</p>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {[
              { icon: Heart, title: "Meaningful Work", desc: "Every day, you help someone access life-changing healthcare." },
              { icon: Globe, title: "Global Impact", desc: "Work with patients from 15+ countries across Africa, Middle East, and Asia." },
              { icon: Users, title: "Growing Team", desc: "Be part of a fast-growing company with opportunities for leadership." },
            ].map((v, i) => (
              <div key={i} className="text-center" data-testid={`career-value-${i}`}>
                <div className="w-14 h-14 rounded-2xl bg-orange-100 flex items-center justify-center mx-auto mb-4">
                  <v.icon className="h-7 w-7 text-orange-600" />
                </div>
                <h3 className="font-heading text-lg font-semibold text-slate-900 mb-2">{v.title}</h3>
                <p className="text-slate-500 text-sm">{v.desc}</p>
              </div>
            ))}
          </div>

          <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-8 text-center">Current Openings</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-16">
            {openings.map((o, i) => (
              <Card key={i} className="bg-white rounded-xl border border-slate-100 p-6 card-hover" data-testid={`job-opening-${i}`}>
                <div className="flex items-start justify-between mb-3">
                  <h3 className="font-heading text-lg font-semibold text-slate-900">{o.title}</h3>
                  <Badge variant="outline" className="text-xs border-orange-200 text-orange-700 shrink-0">{o.type}</Badge>
                </div>
                <div className="flex items-center gap-1 text-slate-500 text-sm mb-2">
                  <MapPin className="h-3.5 w-3.5" /> {o.location}
                </div>
                <p className="text-slate-500 text-sm">{o.desc}</p>
              </Card>
            ))}
          </div>

          <div className="max-w-2xl mx-auto">
            <Card className="bg-white rounded-2xl p-8 border border-slate-100 shadow-sm" data-testid="career-form-card">
              {success ? (
                <div className="flex flex-col items-center py-8 gap-4">
                  <CheckCircle className="h-16 w-16 text-orange-600" />
                  <h3 className="font-heading text-2xl font-semibold">Application Submitted!</h3>
                  <p className="text-slate-500 text-center text-sm">We'll review your application and get back to you soon.</p>
                  <Button onClick={() => setSuccess(false)} className="bg-orange-600 text-white rounded-full mt-2">Apply for Another Role</Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="grid gap-5" data-testid="career-form">
                  <h2 className="font-heading text-2xl font-semibold text-slate-900 text-center">Apply Now</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="grid gap-2">
                      <Label>Full Name *</Label>
                      <Input required value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} placeholder="Your name" data-testid="career-name" />
                    </div>
                    <div className="grid gap-2">
                      <Label>Email *</Label>
                      <Input type="email" required value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} placeholder="your@email.com" data-testid="career-email" />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="grid gap-2">
                      <Label>Phone *</Label>
                      <Input required value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} placeholder="+1 234 567 890" data-testid="career-phone" />
                    </div>
                    <div className="grid gap-2">
                      <Label>Position *</Label>
                      <Select onValueChange={(v) => setForm({...form, position: v})}>
                        <SelectTrigger data-testid="career-position"><SelectValue placeholder="Select role" /></SelectTrigger>
                        <SelectContent>
                          {openings.map((o) => <SelectItem key={o.title} value={o.title}>{o.title}</SelectItem>)}
                          <SelectItem value="Other">Other / General Application</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid gap-2">
                    <Label>Experience *</Label>
                    <Select onValueChange={(v) => setForm({...form, experience: v})}>
                      <SelectTrigger data-testid="career-experience"><SelectValue placeholder="Years of experience" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0-1 years">0-1 years</SelectItem>
                        <SelectItem value="1-3 years">1-3 years</SelectItem>
                        <SelectItem value="3-5 years">3-5 years</SelectItem>
                        <SelectItem value="5-10 years">5-10 years</SelectItem>
                        <SelectItem value="10+ years">10+ years</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid gap-2">
                    <Label>Cover Letter / Message</Label>
                    <Textarea value={form.message} onChange={(e) => setForm({...form, message: e.target.value})} placeholder="Tell us about yourself..." rows={4} data-testid="career-message" />
                  </div>
                  <Button type="submit" disabled={loading} className="bg-orange-600 hover:bg-orange-700 text-white rounded-full font-semibold h-12" data-testid="career-submit-btn">
                    {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Briefcase className="h-4 w-4 mr-2" />}
                    {loading ? "Submitting..." : "Submit Application"}
                  </Button>
                </form>
              )}
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
}
